import { Box, Button, Select, useDisclosure } from "@chakra-ui/react";
import { NumberInput, RemoveButton, TextField } from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  CommissionUnit,
  ProjectTypeEnum,
} from "@src/__generated__/urql-graphql";
import { CircleTickIcon } from "@src/components/ui-kit/Icon";
import { ModalCompleteExpense } from "@src/components/widgets/Modals/ModalCompleteExpense";
import { ModalInvoices } from "@src/components/widgets/Modals/ModalInvoices";
import { currency } from "@src/utils/formatters";
import {
  fieldNumberModelToInputProps,
  fieldToInputProps,
} from "@src/utils/forms/inputHelpers";
import { useStore as useAppStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import { ExpenseBudgetForm, OurWorkBudgetForm } from "../forms";
import { useStore } from "../store";

interface CategoryItemProps {
  isFirst: boolean;
  item: OurWorkBudgetForm | ExpenseBudgetForm;
  onRemove: () => void;
}

const CategoryItem = ({ isFirst, item, onRemove }: CategoryItemProps) => {
  const { workspaceStore } = useAppStore();
  const store = useStore();
  const isTimeAndMaterials =
    store.project?.type === ProjectTypeEnum.TimeMaterials;

  const onSelectCommissionUnit = (newValue: CommissionUnit) => {
    if (item instanceof OurWorkBudgetForm) return;

    item.commission_unit.onChange(newValue);

    if (newValue === CommissionUnit.Relative) {
      item.planned_commission.reset(
        new NumberModel(item.planned_commission.value.denominatedValue, 2),
      );
    } else if (newValue === CommissionUnit.Absolute) {
      item.planned_commission.reset(
        new NumberModel(
          item.planned_commission.value.denominatedValue,
          workspaceStore.settings!.currency.denomination,
        ),
      );
    }
  };

  const modalInvoices = useDisclosure();
  const modalCompleteEbi = useDisclosure();

  return (
    <Box borderTopWidth={isFirst ? 0 : "0.1rem"} paddingBlock="4">
      <section className="space-y-4 py-4">
        <div className="flex justify-between space-x-24">
          <div className="w-2/5">
            <TextField
              label={t`Budget item name`}
              type="text"
              {...fieldToInputProps(item.title)}
            />
          </div>
          <div className="w-1/5">
            {item instanceof ExpenseBudgetForm && store.project?.billable && (
              <NumberInput
                currency
                label={t`Planned expenses`}
                {...fieldNumberModelToInputProps(item.planned_expenses)}
              />
            )}
          </div>
          <div className="w-1/5">
            {item instanceof ExpenseBudgetForm && store.project?.billable && (
              <NumberInput
                currency={
                  item.commission_unit.value === CommissionUnit.Absolute
                }
                suffix={" "}
                label={t`Planned commission`}
                {...fieldNumberModelToInputProps(item.planned_commission)}
                connectedRight={
                  <Select
                    border="none"
                    onChange={(event) =>
                      onSelectCommissionUnit(
                        event.target.value as CommissionUnit,
                      )
                    }
                    value={item.commission_unit.value}
                  >
                    <option value={CommissionUnit.Absolute}>
                      {workspaceStore.settings!.currency_symbol}
                    </option>
                    <option value={CommissionUnit.Relative}>%</option>
                  </Select>
                }
              />
            )}
          </div>
          <div className="w-1/5">
            <NumberInput
              currency
              disabled={isTimeAndMaterials}
              label={t`Budget`}
              {...fieldNumberModelToInputProps(item.total_budget)}
              connectedRight={
                <div className="flex h-full ml-8">
                  <RemoveButton
                    onClick={onRemove}
                    isDisabled={isTimeAndMaterials}
                  />
                </div>
              }
            />
          </div>
        </div>
        {!!item.id && (
          <div>
            {store.project?.billable && (
              <div>
                <Button onClick={modalInvoices.onOpen} variant="link">
                  <span className="text-inkLightest">
                    {t`Invoiced`}
                    {`: ${currency.formatByWorkspace(item.credit)}`}
                  </span>
                </Button>
                <ModalInvoices
                  {...modalInvoices}
                  ourWorkBudgetItemId={
                    item instanceof OurWorkBudgetForm ? item.id : undefined
                  }
                  expenseBudgetIdemId={
                    item instanceof ExpenseBudgetForm ? item.id : undefined
                  }
                  onTransfer={store.refetch}
                />
              </div>
            )}
            {item instanceof ExpenseBudgetForm && (
              <div className="flex items-center">
                <Button onClick={modalCompleteEbi.onOpen} variant="link">
                  {t`Expenses: ${currency.formatByWorkspace(item.real_expenses)}`}
                </Button>
                {item.isCompleted && (
                  <div className="ml-3">
                    <CircleTickIcon />
                  </div>
                )}

                <ModalCompleteExpense
                  id={item.id}
                  projectId={store.project?.id}
                  {...modalCompleteEbi}
                  onAction={(data) => item.setCompletedAt(data?.completed_at)}
                />
              </div>
            )}
          </div>
        )}
      </section>
    </Box>
  );
};

export default observer(CategoryItem);
